import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ROMPSLOMP_MERGE_FIELDS, TEMPLATE_KINDS, TEMPLATE_MERGE_FIELDS, type Template } from '@aviaconsole/shared';
import { capi } from '../api/client';
import { useAuth } from '../auth/AuthProvider';
import { useI18n } from '../i18n';
import { formatDate } from '../lib/format';
import { useToast } from '../components/Toast';
import { Button, Card, Chip, Empty, Input, Loading, Modal, PageHeader, Select, Textarea, cap } from '../components/ui';
import { LayoutEditor } from '../components/LayoutEditor';

type Form = { name: string; kind: Template['kind']; status: Template['status']; subject: string; bodyText: string; pdfLayout: Template['pdfLayout'] };
const blank = (): Form => ({ name: '', kind: 'invoice', status: 'draft', subject: '', bodyText: '', pdfLayout: 'standard' });

export function Templates() {
  const { company, role } = useAuth();
  const { t, s, lang } = useI18n();
  const qc = useQueryClient();
  const toast = useToast();
  const q = useQuery({ queryKey: ['templates', company?.id], queryFn: () => capi<Template[]>('/templates') });
  const [editing, setEditing] = useState<{ id: string | null; form: Form } | null>(null);
  const save = useMutation({
    mutationFn: ({ id, form }: { id: string | null; form: Form }) => (id ? capi<Template>(`/templates/${id}`, { method: 'PATCH', json: form }) : capi<Template>('/templates', { method: 'POST', json: form })),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['templates'] }); setEditing(null); toast.push(t('done')); },
    onError: (e: Error) => toast.push(e.message, 'error'),
  });
  const loadDefaults = useMutation({
    mutationFn: () => capi<Template[]>('/templates/defaults', { method: 'POST' }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['templates'] }); toast.push(t('done')); },
    onError: (e: Error) => toast.push(e.message, 'error'),
  });
  const del = useMutation({ mutationFn: (id: string) => capi(`/templates/${id}`, { method: 'DELETE' }), onSuccess: () => { qc.invalidateQueries({ queryKey: ['templates'] }); setEditing(null); } });
  const open = (tp?: Template) => setEditing(tp ? { id: tp.id, form: { name: tp.name, kind: tp.kind, status: tp.status, subject: tp.subject ?? '', bodyText: tp.bodyText ?? '', pdfLayout: tp.pdfLayout } } : { id: null, form: blank() });
  return (
    <>
      <PageHeader crumb={t('tplCrumb')} title={t('tplTitle')} sub={t('tplSub')} actions={role === 'admin' && <><Button icon="fas fa-file-import" onClick={() => loadDefaults.mutate()} disabled={loadDefaults.isPending}>{t('loadDefaultTexts')}</Button><Button variant="primary" icon="fas fa-plus" onClick={() => open()}>{t('tplAction')}</Button></>} />
      {!q.data ? <Loading /> : (
        <div className="grid two" style={{ gridTemplateColumns: 'minmax(0, 1.6fr) minmax(0, 1fr)' }}>
          <Card pad={false} className="table">
            <table>
              <thead><tr><th>{t('colTemplate')}</th><th>{t('colType')}</th><th>{t('colVersion')}</th><th>{t('colOutputs')}</th><th className="r">{t('colStatus')}</th></tr></thead>
              <tbody>
                {q.data.length === 0 && <Empty />}
                {q.data.map((tp) => (
                  <tr key={tp.id} className="click" onClick={() => open(tp)}>
                    <td><div style={{ fontWeight: 600 }}>{tp.name}</div><div className="sub">{t('updated', { when: formatDate(tp.updatedAt, lang) })}</div></td>
                    <td>{s(cap(tp.kind))}</td>
                    <td className="mono small">Rev {tp.version}</td>
                    <td className="secondary small">{tp.kind === 'invoice' ? 'PDF · e-Factuur XML' : 'PDF · mail body'}</td>
                    <td className="r"><Chip tone={tp.status === 'live' ? 'success' : 'warning'}>{s(cap(tp.status))}</Chip></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
          <div className="stack">
            <Card>
              <h2 style={{ marginBottom: '0.3rem' }}>{t('hMergeFields')}</h2>
              <p className="secondary" style={{ margin: '0 0 0.8rem', fontSize: '0.8rem' }}>{t('mergeHint')}</p>
              <div className="row" style={{ gap: '0.4rem' }}>{TEMPLATE_MERGE_FIELDS.map((v) => <span key={v} className="var">{`{{${v}}}`}</span>)}</div>
              <p className="secondary" style={{ margin: '0.9rem 0 0.5rem', fontSize: '0.8rem', textWrap: 'pretty' }}>{t('rompslompFieldsHint')}</p>
              <div className="row" style={{ gap: '0.4rem' }}>{Object.keys(ROMPSLOMP_MERGE_FIELDS).map((v) => <span key={v} className="var">{`{${v}}`}</span>)}<span className="var">{'{if veld}…{endif}'}</span></div>
            </Card>
            <LayoutEditor />
          </div>
        </div>
      )}
      {editing && (
        <Modal title={editing.id ? editing.form.name : t('tplAction')} onClose={() => setEditing(null)}>
          <form className="stack md" onSubmit={(e) => { e.preventDefault(); save.mutate(editing); }}>
            <Input label={t('templateName')} required value={editing.form.name} onChange={(e) => setEditing({ ...editing, form: { ...editing.form, name: e.target.value } })} />
            <div className="grid fields">
              <Select label={t('kind')} value={editing.form.kind} options={TEMPLATE_KINDS.map((k) => ({ value: k, label: s(cap(k)) }))} onChange={(e) => setEditing({ ...editing, form: { ...editing.form, kind: e.target.value as Template['kind'] } })} />
              <Select label={t('status')} value={editing.form.status} options={[{ value: 'draft', label: s('Draft') }, { value: 'live', label: s('Live') }]} onChange={(e) => setEditing({ ...editing, form: { ...editing.form, status: e.target.value as Template['status'] } })} />
              <Select label={t('pdfLayout')} value={editing.form.pdfLayout} options={[{ value: 'standard', label: 'Standard NL' }, { value: 'reverse_charge', label: 'Reverse charge / ICP' }]} onChange={(e) => setEditing({ ...editing, form: { ...editing.form, pdfLayout: e.target.value as Template['pdfLayout'] } })} />
            </div>
            <Input label={t('mailSubject')} value={editing.form.subject} onChange={(e) => setEditing({ ...editing, form: { ...editing.form, subject: e.target.value } })} />
            <Textarea label={t('mailBody')} value={editing.form.bodyText} style={{ minHeight: 200 }} onChange={(e) => setEditing({ ...editing, form: { ...editing.form, bodyText: e.target.value } })} />
            <div className="row" style={{ justifyContent: 'flex-end' }}>
              {editing.id && role === 'admin' && <Button variant="danger" onClick={() => { if (confirm(t('confirmDelete'))) del.mutate(editing.id!); }}>{t('delete')}</Button>}
              <div className="spacer" />
              <Button onClick={() => setEditing(null)}>{t('cancel')}</Button>
              {role === 'admin' && <Button variant="primary" type="submit" disabled={save.isPending}>{t('save')}</Button>}
            </div>
          </form>
        </Modal>
      )}
    </>
  );
}
